import { store } from 'store';
import { selectSessionToken } from 'features/onboarding/state/selectors';

const KEY_PREFIX = 'govt_id_v2_journey:';

const emptyJourney = () => ({
    started_at: null,
    ended: false,
    on_flow: false,
    step: null,
    screen: null,
    step_at: null,
    active_ms: 0,
    visible_since: null,
    counters: {},
    issue_codes: [],
    last_issue: null,
    last_hint: null,
    selfie_outcome: null,
});

/**
 * Kept in sessionStorage rather than the store. Every page load resets the store, and the govtIdV2
 * slice is left unpersisted so the ID images never reach storage. A reset can't tell a reload from
 * a new link, and persisting writes too late for a closing tab. Keyed by link, a reload keeps the
 * journey and a new link starts clean.
 */
const storageKey = () => {
    const linkUuid = selectSessionToken(store.getState());

    return linkUuid ? `${KEY_PREFIX}${linkUuid}` : null;
};

export const getJourney = () => {
    try {
        const key = storageKey();
        const stored = key && sessionStorage.getItem(key);

        return stored ? { ...emptyJourney(), ...JSON.parse(stored) } : emptyJourney();
    } catch {
        return emptyJourney();
    }
};

const update = (change) => {
    const journey = { ...getJourney(), ...change };

    try {
        const key = storageKey();
        if (key) sessionStorage.setItem(key, JSON.stringify(journey));
    } catch {
        // Private mode or a full quota: the journey simply isn't carried across reloads.
    }

    return journey;
};

const isJourneyOpen = (journey) => journey.started_at !== null && !journey.ended;

export const isOpen = () => isJourneyOpen(getJourney());

export const isOnFlow = () => getJourney().on_flow;

export const setOnFlow = (onFlow) => update({ on_flow: onFlow });

export const startJourney = (now = Date.now()) => {
    const { on_flow, step, screen, step_at } = getJourney();

    return update({
        ...emptyJourney(),
        on_flow,
        step,
        screen,
        step_at,
        started_at: now,
        visible_since: now,
    });
};

export const getJourneyElapsedMs = (now = Date.now()) => {
    const { started_at } = getJourney();

    return started_at === null ? undefined : now - started_at;
};

// `screen` tells error screens apart, which all share the `error` step.
export const recordStep = (step, screen = null, now = Date.now()) => {
    const journey = getJourney();

    if (journey.step === step && journey.screen === screen) return null;

    update({ step, screen, step_at: now });

    return {
        previous_step: journey.step,
        previous_step_ms: journey.step_at === null ? undefined : now - journey.step_at,
    };
};

export const getCount = (name) => getJourney().counters[name] ?? 0;

export const nextCount = (name) => {
    const { counters } = getJourney();
    const count = (counters[name] ?? 0) + 1;

    update({ counters: { ...counters, [name]: count } });

    return count;
};

export const recordIssue = ({ code, severity }) => {
    const { issue_codes } = getJourney();

    update({
        issue_codes: issue_codes.includes(code) ? issue_codes : [...issue_codes, code],
        last_issue: { code, severity },
    });
};

export const setLastHint = (hint) => update({ last_hint: hint });

export const setSelfieOutcome = (outcome) => update({ selfie_outcome: outcome });

export const pauseActive = (now = Date.now()) => {
    const { active_ms, visible_since } = getJourney();

    if (visible_since === null) return;

    update({ active_ms: active_ms + (now - visible_since), visible_since: null });
};

export const resumeActive = (now = Date.now()) => {
    const journey = getJourney();

    if (!isJourneyOpen(journey) || journey.visible_since !== null) return;

    update({ visible_since: now });
};

// Returns null when no journey is open, so a terminal screen reloaded after the end can't end it twice.
export const endJourney = (now = Date.now()) => {
    if (!isOpen()) return null;

    pauseActive(now);

    const journey = update({ ended: true });

    return {
        issue_codes_seen: journey.issue_codes,
        last_issue_code: journey.last_issue?.code,
        selfie_outcome: journey.selfie_outcome,
        active_ms_this_device: journey.active_ms,
        captures: journey.counters.captures ?? 0,
        selfie_sessions: journey.counters.selfie_sessions ?? 0,
        journey_elapsed_ms: now - journey.started_at,
    };
};
